import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import { DisplayTemplate } from '@crossnokaye/typescript-sdk/models/components';

import CurrentFacilityAtoms from '@/state/current-facility.state';

/**
 * Type for display template preview storage
 * Includes facility shortname to detect facility changes and auto-clear
 */
export type DisplayTemplatePreviewStorage = {
  facilityShortname: string;
  jsonContent: string;
} | null;

/**
 * Base storage atom for display template preview
 */
const displayTemplatePreviewStorageBaseAtom = atomWithStorage<DisplayTemplatePreviewStorage>(
  'ux-facility-config-preview',
  null
);

/**
 * Atom that stores the display template preview JSON string and facility shortname from localStorage.
 * Automatically clears when the facility changes.
 */
export const displayTemplatePreviewStorageAtom = atom(
  (get) => {
    const storage = get(displayTemplatePreviewStorageBaseAtom);
    const currentFacility = get(CurrentFacilityAtoms.facility);

    // Auto-clear if facility has changed
    if (
      storage &&
      currentFacility?.shortName &&
      storage.facilityShortname !== currentFacility.shortName
    ) {
      return null;
    }

    return storage;
  },
  (_, set, newValue: DisplayTemplatePreviewStorage) => {
    set(displayTemplatePreviewStorageBaseAtom, newValue);
  }
);
displayTemplatePreviewStorageAtom.debugLabel = 'displayTemplatePreviewStorageAtom';

/**
 * Derived atom that parses the JSON string into a DisplayTemplate object
 * Returns undefined if the JSON is invalid or null
 */
export const displayTemplatePreviewAtom = atom<DisplayTemplate | undefined>((get) => {
  const storage = get(displayTemplatePreviewStorageAtom);
  if (!storage?.jsonContent) return undefined;

  try {
    return JSON.parse(storage.jsonContent) as DisplayTemplate;
  } catch {
    return undefined;
  }
});
displayTemplatePreviewAtom.debugLabel = 'displayTemplatePreviewAtom';

// ============================================================================
// File Sync State (Session-Only)
// ============================================================================

/**
 * Type for file sync state (session-only, not persisted to storage)
 * Used to track sync status with local ux-facility-config repository
 */
export type FileSyncState = {
  /** Organization shortname when sync was established */
  orgShortname: string;
  /** Facility shortname when sync was established */
  facilityShortname: string;
  /** Display name of the selected directory */
  repoName: string;
  /** Whether currently synced */
  isSynced: boolean;
  /** Last modification time of the synced file */
  lastModified: Date | null;
  /** Error message if sync failed */
  error: string | null;
} | null;

/**
 * Base atom for file sync state (not persisted - session only)
 */
const fileSyncStateBaseAtom = atom<FileSyncState>(null);
fileSyncStateBaseAtom.debugLabel = 'fileSyncStateBaseAtom';

/**
 * Atom that stores the file sync state.
 * Automatically clears when the org or facility changes.
 */
export const fileSyncStateAtom = atom(
  (get) => {
    const state = get(fileSyncStateBaseAtom);
    const currentFacility = get(CurrentFacilityAtoms.facility);
    const currentOrg = get(CurrentFacilityAtoms.organization);

    // Auto-clear if org or facility has changed
    if (
      state &&
      (state.orgShortname !== currentOrg?.displayName ||
        state.facilityShortname !== currentFacility?.shortName)
    ) {
      return null;
    }
    return state;
  },
  (_, set, newValue: FileSyncState) => {
    set(fileSyncStateBaseAtom, newValue);
  }
);
fileSyncStateAtom.debugLabel = 'fileSyncStateAtom';
